import { SupportPermissions } from "@redotech/account-common/permissions";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { type PhoneNumber } from "@redotech/redo-common-model/phone/phone-number";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { VoiceProvider } from "@redotech/redo-support-settings-common/voice-settings";
import {
  isPhoneWidgetHiddenByUser,
  usePhoneWidget,
} from "@redotech/redo-web/voice/shared/phone-widget-context";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PhoneNumberContext } from "../../services/phone-number-service";
import { useIntegrations } from "../hooks/use-integrations";
import { PhoneWidgetV2 } from "./phone-widget-v2";
import { VoiceContextV2Provider } from "./voice-context-v2-provider";
import * as voiceWidgetCss from "./voice-widget-v2.module.css";

/**
 * Top-level mount for the v2 voice widget. Mirrors v1's `<VoiceWidget>`
 * gating: requires SUPPORT permission, a configured Telnyx voice
 * integration, voice enabled in team settings, and active billing.
 * Aircall is not supported in v2 — Aircall teams continue to use the
 * v1 widget.
 *
 * v1 used `phoneNumber.voiceMetadata` to detect "voice is set up". v2
 * stores that provisioning state on the `telnyx_voice` shopper-ai
 * integration instead, so we look for an active integration here. The
 * phone number is still needed to render the widget (caller-id), but
 * we no longer require it to carry `voiceMetadata`.
 */
export const VoiceWidgetV2 = memo(function VoiceWidgetV2() {
  const team = useRequiredContext(TeamContext);
  const [phoneNumber] = useLazyContext(PhoneNumberContext);
  const { setVoiceAvailable, show } = usePhoneWidget();
  const { data: integrations } = useIntegrations();

  const hasSupportPermission = useHasPermission(
    SupportPermissions.VIEW_SUPPORT,
  );

  const voiceProvider =
    team.settings.support?.voice?.provider ?? VoiceProvider.TELNYX;

  const hasVoiceIntegration = (integrations ?? []).some(
    (i) => i.integrationType === "telnyx_voice",
  );

  const isTelnyxAvailable = !!(
    phoneNumber.value &&
    hasVoiceIntegration &&
    team.settings.support?.voice?.enabled &&
    team.settings.support?.billing?.voice?.subscriptionActive
  );
  const isVoiceAvailable =
    voiceProvider === VoiceProvider.TELNYX && isTelnyxAvailable;

  useEffect(() => {
    setVoiceAvailable(isVoiceAvailable);
    if (isVoiceAvailable && !isPhoneWidgetHiddenByUser()) {
      show();
    }
  }, [isVoiceAvailable, setVoiceAvailable, show]);

  if (!isVoiceAvailable || !hasSupportPermission) {
    return null;
  }
  if (!phoneNumber.value) {
    return null;
  }

  return (
    <VoiceContextV2Provider>
      <Mount phoneNumber={phoneNumber.value} />
    </VoiceContextV2Provider>
  );
});

const POSITION_STORAGE_KEY = "redo-voice-widget-v2-position";
const PADDING = 16;
const INTERACTIVE_SELECTOR =
  "button, a, input, textarea, select, [role='button'], [role='menuitem'], [role='tab'], [contenteditable='true']";

interface Position {
  readonly left: number;
  readonly top: number;
}

/**
 * Drag bounds: anywhere right of the *primary* side nav and below the
 * top nav. The user may also drop the widget over the secondary nav
 * strip (when present) — that area is intentionally unreserved, so we
 * deliberately ignore the secondary nav width.
 *
 * `--redo-primary-nav-width` is published by `redo-primary-nav.tsx` and
 * tracks the live width of the primary rail (drag-resize, collapse,
 * etc.). `--redo-side-nav-right` would be wrong here because it
 * includes the secondary nav.
 */
const computeBounds = (
  el: HTMLElement,
): { minLeft: number; maxLeft: number; minTop: number; maxTop: number } => {
  const styles = getComputedStyle(document.documentElement);
  const primaryNavWidth = parseFloat(
    styles.getPropertyValue("--redo-primary-nav-width"),
  );
  const fallbackSideNavWidth =
    parseFloat(styles.getPropertyValue("--redo-web-page-side-nav-width")) ||
    240;
  const leftEdge = Number.isFinite(primaryNavWidth)
    ? primaryNavWidth
    : fallbackSideNavWidth;
  const topNavHeight =
    parseFloat(styles.getPropertyValue("--redo-top-nav-height")) || 48;
  const minLeft = leftEdge + PADDING;
  const minTop = topNavHeight + PADDING;
  const maxLeft = Math.max(
    minLeft,
    window.innerWidth - el.offsetWidth - PADDING,
  );
  const maxTop = Math.max(
    minTop,
    window.innerHeight - el.offsetHeight - PADDING,
  );
  return { minLeft, maxLeft, minTop, maxTop };
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const loadStoredPosition = (): Position | null => {
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { left?: unknown; top?: unknown };
    if (typeof parsed.left !== "number" || typeof parsed.top !== "number") {
      return null;
    }
    return { left: parsed.left, top: parsed.top };
  } catch {
    return null;
  }
};

const Mount = memo(function Mount({
  phoneNumber,
}: {
  phoneNumber: PhoneNumber;
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // The persisted, user-intended position — interpreted as the
  // collapsed pill's top-left. Set by drag and on initial load. Never
  // mutated by expand/collapse so the widget springs back to where the
  // user left it once it shrinks again.
  const [position, setPosition] = useState<Position | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Bumped whenever the wrapper's measured size changes (expand /
  // collapse / content reflow). Used as a dependency to recompute the
  // displayed position from `position` against the live bounds.
  const [sizeNonce, setSizeNonce] = useState(0);
  // Smallest height observed for the wrapper — i.e., the collapsed
  // pill's height. Used as the bottom-anchor reference so expansion
  // grows upward and the collapsed pill returns to the saved spot.
  const collapsedHeightRef = useRef<number | null>(null);

  // Initial placement: restore from storage if valid, else bottom-left
  // of the content area (matches v1's default).
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    const bounds = computeBounds(el);
    const stored = loadStoredPosition();
    const initial: Position = stored
      ? {
          left: clamp(stored.left, bounds.minLeft, bounds.maxLeft),
          top: clamp(stored.top, bounds.minTop, bounds.maxTop),
        }
      : { left: bounds.minLeft, top: bounds.maxTop };
    setPosition(initial);
  }, []);

  // Track the wrapper's size so the displayed position can be
  // re-clamped when the widget expands / collapses without mutating
  // the saved position. Also captures the smallest observed height as
  // the collapsed reference point.
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height;
      if (typeof h === "number" && h > 0) {
        collapsedHeightRef.current =
          collapsedHeightRef.current === null
            ? h
            : Math.min(collapsedHeightRef.current, h);
      }
      setSizeNonce((n) => n + 1);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Re-clamp when the viewport resizes so the widget never gets
  // marooned off-screen after a window shrink. (This DOES update the
  // saved position — a viewport shrink is a permanent change to what
  // "valid placements" exist.)
  useEffect(() => {
    const onResize = () => {
      const el = wrapperRef.current;
      if (!el) {
        return;
      }
      setPosition((prev) => {
        if (!prev) {
          return prev;
        }
        const bounds = computeBounds(el);
        return {
          left: clamp(prev.left, bounds.minLeft, bounds.maxLeft),
          top: clamp(prev.top, bounds.minTop, bounds.maxTop),
        };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Persist after each settled drag, normalised to the collapsed-pill
  // bounds. While the widget is expanded, `position` can sit slightly
  // outside the collapsed bounds (we offset for the expansion shift);
  // we clamp before storing so a reload always starts at a valid
  // collapsed placement.
  useEffect(() => {
    if (!position || isDragging) {
      return;
    }
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    const styles = getComputedStyle(document.documentElement);
    const primaryNavWidth = parseFloat(
      styles.getPropertyValue("--redo-primary-nav-width"),
    );
    const fallbackSideNavWidth =
      parseFloat(styles.getPropertyValue("--redo-web-page-side-nav-width")) ||
      240;
    const leftEdge = Number.isFinite(primaryNavWidth)
      ? primaryNavWidth
      : fallbackSideNavWidth;
    const topNavHeight =
      parseFloat(styles.getPropertyValue("--redo-top-nav-height")) || 48;
    const collapsedHeight = collapsedHeightRef.current ?? el.offsetHeight;
    const minLeft = leftEdge + PADDING;
    const minTop = topNavHeight + PADDING;
    const maxLeft = Math.max(
      minLeft,
      window.innerWidth - el.offsetWidth - PADDING,
    );
    const maxTopCollapsed = Math.max(
      minTop,
      window.innerHeight - collapsedHeight - PADDING,
    );
    const normalized: Position = {
      left: clamp(position.left, minLeft, maxLeft),
      top: clamp(position.top, minTop, maxTopCollapsed),
    };
    try {
      localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // Storage may be unavailable (private mode); position resets next
      // load — non-critical.
    }
  }, [position, isDragging]);

  // The position we actually render at: the saved position interpreted
  // as the collapsed pill's top-left, with expansion anchored to the
  // BOTTOM of that collapsed pill so the widget grows upward by
  // default. If growing upward would clip the top bound, we displace
  // downward (clamp to minTop) instead. `position` is never mutated by
  // this — collapse springs the widget back to its saved spot.
  const displayPosition = useMemo<Position | null>(() => {
    if (!position) {
      return null;
    }
    const el = wrapperRef.current;
    if (!el) {
      return position;
    }
    const bounds = computeBounds(el);
    const currentHeight = el.offsetHeight;
    const collapsedHeight = collapsedHeightRef.current ?? currentHeight;
    // Positive when the widget is taller than its collapsed pill.
    const expansionDelta = Math.max(0, currentHeight - collapsedHeight);
    const desiredTop = position.top - expansionDelta;
    return {
      left: clamp(position.left, bounds.minLeft, bounds.maxLeft),
      top: clamp(desiredTop, bounds.minTop, bounds.maxTop),
    };
    // sizeNonce is a deliberate dep — re-derive when the widget resizes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, sizeNonce]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // Don't start a drag if the user is interacting with a control
    // inside the widget (button, input, etc.).
    if (target.closest(INTERACTIVE_SELECTOR)) {
      return;
    }
    const el = wrapperRef.current;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    setIsDragging(true);

    const onPointerMove = (ev: PointerEvent) => {
      const widget = wrapperRef.current;
      if (!widget) {
        return;
      }
      const bounds = computeBounds(widget);
      const currentHeight = widget.offsetHeight;
      const collapsedHeight = collapsedHeightRef.current ?? currentHeight;
      const expansionDelta = Math.max(0, currentHeight - collapsedHeight);
      // The cursor controls the visual top-left. Convert that back to
      // the saved collapsed-pill position by adding the expansion
      // shift, so collapsing later returns the widget to the same
      // bottom-anchor the user just dragged.
      const visualTop = clamp(
        ev.clientY - offsetY,
        bounds.minTop,
        bounds.maxTop,
      );
      setPosition({
        left: clamp(ev.clientX - offsetX, bounds.minLeft, bounds.maxLeft),
        top: visualTop + expansionDelta,
      });
    };

    const onPointerUp = () => {
      setIsDragging(false);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
  }, []);

  return (
    <div
      className={`${voiceWidgetCss.phone} ${
        isDragging ? voiceWidgetCss.phoneDragging : ""
      }`}
      onPointerDown={onPointerDown}
      ref={wrapperRef}
      style={
        displayPosition
          ? { left: displayPosition.left, top: displayPosition.top }
          : // Hide until first measurement to avoid a flash at (0, 0).
            { visibility: "hidden" }
      }
    >
      <PhoneWidgetV2 merchantPhoneNumber={phoneNumber.number} />
    </div>
  );
});
